import { useEffect, useMemo, useState } from 'react'
import {
  fetchPendingApprovals,
  fetchAttentionRooms,
  normalizePendingApprovalEntry,
  openAttentionChatRoom,
  openAttentionSuggestion,
  openChatThread,
  sendMarkChatRoomRead,
  sendMarkChatThreadRead,
  sendPermissionResponse,
} from '../../api/ws'
import { sendPermissionResponse as sendPermissionResponseHTTP } from '../../api/client'
import { useAppStore } from '../../store/app'
import { PermissionRequestCard } from '../permission/PermissionRequestCard'
import { IdlePromptCard } from '../permission/IdlePromptCard'
import { SwipeableRow } from '../../components/SwipeableRow'
import type {
  AttentionRoom,
  AttentionSuggestion,
  AttentionThread,
  PermissionRequest,
  PermissionDecision,
  PermissionDecisionBehavior,
} from '../../store/types'
import './AttentionSheet.css'

// The Attention Inbox — one cross-household home for everything waiting on a
// person: approvals to answer, chat rooms to read, followed threads with new
// replies (Slack's "Threads" view, folded in here), and setup suggestions to
// review. Grouped by household
// (current first). Chat rows navigate + clear on tap; approval rows compress to
// a line with tap-through Allow / Deny and expand the full card in place.
// Swipe declines (approve is always a tap). See the Attention Inbox proposal.

type Segment = 'all' | 'approvals' | 'chat'

export function AttentionSheet() {
  const pendingRequests = useAppStore((s) => s.pendingRequests)
  const attentionRooms = useAppStore((s) => s.attentionRooms)
  const attentionThreads = useAppStore((s) => s.attentionThreads)
  const attentionSuggestions = useAppStore((s) => s.attentionSuggestions)
  const myInvites = useAppStore((s) => s.myInvites)
  const availableOrganizations = useAppStore((s) => s.availableOrganizations)
  const enqueueRequest = useAppStore((s) => s.enqueueRequest)
  const dequeueRequest = useAppStore((s) => s.dequeueRequest)
  const setAttentionRooms = useAppStore((s) => s.setAttentionRooms)
  const pushSheet = useAppStore((s) => s.pushSheet)
  const popSheet = useAppStore((s) => s.popSheet)

  const [segment, setSegment] = useState<Segment>('all')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [sendError, setSendError] = useState<string | null>(null)

  // Load both halves when the inbox opens: approvals (cross-household, merged
  // into the store) and the unread-rooms list.
  useEffect(() => {
    let cancelled = false
    void Promise.allSettled([
      fetchPendingApprovals().then((r) => {
        if (cancelled || !r?.entries) return
        const existing = new Set(useAppStore.getState().pendingRequests.map((p) => p.id))
        for (const e of r.entries) {
          if (!existing.has(e.request_id)) enqueueRequest(normalizePendingApprovalEntry(e))
        }
      }),
      fetchAttentionRooms(),
    ]).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [enqueueRequest])

  const currentOrgID = availableOrganizations.find((o) => o.is_current)?.id
  const orgName = (id?: string) =>
    availableOrganizations.find((o) => o.id === id)?.name

  // Approvals are cross-household (pendingRequests is filled by the canonical
  // list_pending_approvals pull, which scans by approver with no org filter).
  const approvals = pendingRequests

  // Group approvals + rooms by household, current household first.
  const groups = useMemo(() => {
    const map = new Map<
      string,
      {
        orgID: string
        name: string
        approvals: PermissionRequest[]
        rooms: AttentionRoom[]
        threads: AttentionThread[]
        suggestions: AttentionSuggestion[]
      }
    >()
    const ensure = (orgID: string, name: string) => {
      let g = map.get(orgID)
      if (!g) {
        g = { orgID, name, approvals: [], rooms: [], threads: [], suggestions: [] }
        map.set(orgID, g)
      }
      return g
    }
    if (segment !== 'chat') {
      for (const a of approvals) {
        const id = a.organization_id || 'unknown'
        ensure(id, a.organization_name || orgName(id) || 'Household').approvals.push(a)
      }
    }
    // Setup suggestions are neither approvals nor chat, so like invitations they
    // show only under All.
    if (segment === 'all') {
      for (const p of attentionSuggestions) {
        ensure(
          p.organization_id,
          p.organization_name || orgName(p.organization_id) || 'Household',
        ).suggestions.push(p)
      }
    }
    if (segment !== 'approvals') {
      for (const r of attentionRooms) {
        ensure(
          r.organization_id,
          r.organization_name || orgName(r.organization_id) || 'Household',
        ).rooms.push(r)
      }
      for (const t of attentionThreads) {
        ensure(
          t.organization_id,
          t.organization_name || orgName(t.organization_id) || 'Household',
        ).threads.push(t)
      }
    }
    return [...map.values()].sort((a, b) => {
      if (a.orgID === currentOrgID) return -1
      if (b.orgID === currentOrgID) return 1
      return a.name.localeCompare(b.name)
    })
  }, [approvals, attentionRooms, attentionThreads, attentionSuggestions, segment, currentOrgID]) // eslint-disable-line react-hooks/exhaustive-deps

  async function respond(
    reqID: string,
    decision: PermissionDecision,
    extras?: { selectedOptionID?: string; freeText?: string },
  ) {
    setSendError(null)
    const args = {
      requestID: reqID,
      behavior: decision.behavior,
      expiresAt: decision.expires_at,
      interrupt: decision.interrupt,
      selectedOptionID: extras?.selectedOptionID,
      freeText: extras?.freeText,
    }
    // WS primary, HTTP fallback — only drop the row on a confirmed send, same
    // as the standalone approvals sheet.
    const delivered = sendPermissionResponse(args)
    if (!delivered) {
      try {
        await sendPermissionResponseHTTP(args)
      } catch {
        setSendError('Couldn’t reach the server — your response wasn’t sent. Try again.')
        return
      }
    }
    if (expanded === reqID) setExpanded(null)
    dequeueRequest(reqID)
  }

  function shortcut(
    reqID: string,
    behavior: PermissionDecisionBehavior,
    extras?: { selectedOptionID?: string; freeText?: string },
  ) {
    void respond(
      reqID,
      { behavior, expires_at: null, interrupt: behavior === 'deny_stop' },
      extras,
    )
  }

  function markRead(room: AttentionRoom) {
    void sendMarkChatRoomRead(room.room_id)
    setAttentionRooms(attentionRooms.filter((r) => r.room_id !== room.room_id))
  }

  function openRoom(room: AttentionRoom) {
    openAttentionChatRoom(room.organization_id, room.room_id, room.room_name)
    popSheet()
  }

  function openSuggestion(p: AttentionSuggestion) {
    openAttentionSuggestion(p.organization_id, p.proposal_id)
    popSheet()
  }

  // Opening a thread lands you in it over its room (Back → the room); the
  // thread screen marks it read.
  function openThread(t: AttentionThread) {
    openChatThread(t.organization_id, t.room_id, t.room_name, t.root_id)
    popSheet()
  }

  // Invitations aren't a household you're in, so they don't group under one —
  // they sit in a "For you" section, and only under All (they're neither an
  // approval nor a chat). Accept/decline lives in the invitations sheet.
  function openInvites() {
    popSheet()
    pushSheet({ kind: 'invitations' })
  }
  const showInvites = segment === 'all' && myInvites.length > 0

  const total =
    approvals.length +
    attentionRooms.length +
    attentionThreads.length +
    attentionSuggestions.length +
    myInvites.length

  if (loading && total === 0) {
    return <div className="attn__state">Loading…</div>
  }

  if (total === 0) {
    return (
      <div className="attn__state attn__state--empty">
        <div className="attn__state-icon">✓</div>
        <div className="attn__state-title">All caught up</div>
        <div className="attn__state-sub">Nothing is waiting on you.</div>
        <button className="attn__done" onClick={() => popSheet()}>
          Done
        </button>
      </div>
    )
  }

  return (
    <div className="attn">
      <div className="attn__segments" role="tablist">
        {(['all', 'approvals', 'chat'] as Segment[]).map((s) => (
          <button
            key={s}
            role="tab"
            aria-selected={segment === s}
            className={`attn__seg${segment === s ? ' attn__seg--on' : ''}`}
            onClick={() => setSegment(s)}
          >
            {s === 'all' ? 'All' : s === 'approvals' ? 'Approvals' : 'Chat'}
          </button>
        ))}
      </div>

      {sendError && (
        <div role="alert" className="attn__error">
          {sendError}
        </div>
      )}

      <div className="attn__scroll">
        {showInvites && (
          <section className="attn__group">
            <div className="attn__group-head">
              <span className="attn__group-name">For you</span>
            </div>
            {myInvites.map((inv) => (
              <div key={inv.id} className="attn__item">
                <button className="attn-row attn-row--invite" onClick={openInvites}>
                  <span className="attn-row__icon attn-row__icon--invite">✉️</span>
                  <span className="attn-row__text">
                    <span className="attn-row__title">
                      Join {inv.organization_name || 'a household'}
                    </span>
                    <span className="attn-row__sub">
                      {inv.inviter_email ? `Invited by ${inv.inviter_email}` : 'Household invitation'}
                    </span>
                  </span>
                  <span className="attn-row__chev">›</span>
                </button>
              </div>
            ))}
          </section>
        )}
        {groups.length === 0 && !showInvites && (
          <div className="attn__state-sub attn__state-sub--inline">
            Nothing in this filter.
          </div>
        )}
        {groups.map((g) => (
          <section key={g.orgID} className="attn__group">
            <div className="attn__group-head">
              <span className="attn__group-name">{g.name}</span>
              {g.orgID === currentOrgID && <span className="attn__group-cur">current</span>}
            </div>

            {g.approvals.map((a) => (
              <div key={a.id} className="attn__item">
                <SwipeableRow
                  trailing={{
                    label: 'Deny',
                    tint: 'var(--danger)',
                    onAction: () => shortcut(a.id, 'deny'),
                  }}
                >
                  <div className="attn-row attn-row--ask">
                    <button
                      className="attn-row__main"
                      onClick={() => setExpanded((e) => (e === a.id ? null : a.id))}
                      aria-expanded={expanded === a.id}
                    >
                      <span className="attn-row__icon attn-row__icon--ask">⚡</span>
                      <span className="attn-row__text">
                        <span className="attn-row__title">
                          {a.agent_name || a.position_name || 'Agent'}
                        </span>
                        <span className="attn-row__sub">{a.tool_name}</span>
                      </span>
                      <span className="attn-row__chev">{expanded === a.id ? '⌄' : '›'}</span>
                    </button>
                    <div className="attn-row__acts">
                      <button
                        className="attn-btn attn-btn--allow"
                        onClick={() => shortcut(a.id, 'allow')}
                      >
                        Allow
                      </button>
                      <button
                        className="attn-btn attn-btn--deny"
                        onClick={() => shortcut(a.id, 'deny')}
                      >
                        Deny
                      </button>
                    </div>
                  </div>
                </SwipeableRow>
                {expanded === a.id && (
                  <div className="attn-expand">
                    {a.tool_name.toLowerCase() === 'idle_prompt' ? (
                      <IdlePromptCard request={a} onDismiss={() => dequeueRequest(a.id)} />
                    ) : (
                      <PermissionRequestCard
                        request={a}
                        onShortcut={(behavior, extras) => shortcut(a.id, behavior, extras)}
                        onAlwaysAllow={(expires_at) =>
                          void respond(a.id, {
                            behavior: 'always_allow',
                            expires_at,
                            interrupt: false,
                          })
                        }
                        onAlwaysDeny={(expires_at) =>
                          void respond(a.id, {
                            behavior: 'always_deny',
                            expires_at,
                            interrupt: false,
                          })
                        }
                      />
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Setup suggestions: tap to review. No swipe — a suggestion
                deserves a look before it's applied or declined. */}
            {g.suggestions.map((p) => (
              <div key={p.proposal_id} className="attn__item">
                <button className="attn-row attn-row--suggestion" onClick={() => openSuggestion(p)}>
                  <span className="attn-row__icon attn-row__icon--invite">🧩</span>
                  <span className="attn-row__text">
                    <span className="attn-row__title">{p.title || 'Suggested setup'}</span>
                    <span className="attn-row__sub">
                      {p.status === 'waiting_on_you'
                        ? `Waiting on you — ${p.waiting_steps} step${p.waiting_steps === 1 ? '' : 's'} to do`
                        : `Setup suggestion · ${p.item_count} change${p.item_count === 1 ? '' : 's'} to review`}
                    </span>
                  </span>
                  <span className="attn-row__chev">›</span>
                </button>
              </div>
            ))}

            {g.rooms.map((r) => (
              <div key={r.room_id} className="attn__item">
                <SwipeableRow
                  trailing={{ label: 'Mark read', onAction: () => markRead(r) }}
                >
                  <button className="attn-row attn-row--chat" onClick={() => openRoom(r)}>
                    <span className="attn-row__icon attn-row__icon--chat">💬</span>
                    <span className="attn-row__text">
                      <span className="attn-row__title">{r.room_name}</span>
                      <span className="attn-row__sub">
                        {r.last_sender ? `${r.last_sender}: ` : ''}
                        {r.last_preview || 'New messages'}
                      </span>
                    </span>
                    {r.unread_count > 0 && (
                      <span className="attn-pill">{r.unread_count > 99 ? '99+' : r.unread_count}</span>
                    )}
                  </button>
                </SwipeableRow>
              </div>
            ))}

            {g.threads.map((t) => (
              <div key={t.root_id} className="attn__item">
                <SwipeableRow
                  trailing={{ label: 'Mark read', onAction: () => void sendMarkChatThreadRead(t.root_id) }}
                >
                  <button className="attn-row attn-row--thread" onClick={() => openThread(t)}>
                    <span className="attn-row__icon attn-row__icon--chat">🧵</span>
                    <span className="attn-row__text">
                      <span className="attn-row__title">Thread in {t.room_name}</span>
                      {t.root_preview && (
                        <span className="attn-row__sub attn-row__sub--quote">
                          {t.root_sender ? `${t.root_sender}: ` : ''}
                          {t.root_preview}
                        </span>
                      )}
                      <span className="attn-row__sub">
                        {t.last_sender ? `${t.last_sender}: ` : ''}
                        {t.last_preview || 'New replies'}
                      </span>
                    </span>
                    {t.unread_count > 0 && (
                      <span className="attn-pill">{t.unread_count > 99 ? '99+' : t.unread_count}</span>
                    )}
                  </button>
                </SwipeableRow>
              </div>
            ))}
          </section>
        ))}
      </div>
    </div>
  )
}
